import { Container, Grid, Typography } from "@mui/material";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import React, { useCallback } from "react";
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../../components/ConfirmDialog";
import FormInputDate from "../../components/form/FormInputDate";
import GenericTable, { Column } from "../../components/GenericTable";
import { NotificationResultLog } from "../../data/models/notificationLog";
import { Pagination } from "../../data/models/pagination";
import { AppRoles } from "../../data/models/role";
import { DateRange } from "../../data/models/stats";
import { useStore } from "../../stores/store";
import { hasRolesv2 } from "../../utils/permissionEvaluator";
import { getDateAndTimeFormatByPlatformLanguage } from "../../utils/utils";
import { TenantMenuButton } from "../../_styles/StyledButtons";
import { TableHeader } from "../../_styles/table/TableHeader";
import { TableText } from "../../_styles/table/TableText";
import CustomPopper from "../Client/Dashboard/StudyOverview/StatusPopper";
import { getNotificationLogColumns } from "./getNotificationLogColumns";
import NotificationLogsTableToolbar from "./NotificationLogsTableToolbar";
import ViewHTMLContent from "./ViewHTMLContent";

function NotificationLogsTable() {
    const { i18n } = useTranslation();

    const {
        commonStore: { selectedInstitution },
        templateStore,
        institutionStore,
    } = useStore();
    const { notificationLogStore } = useStore();
    const { loading, logList } = notificationLogStore;
    const { selectedUserInstitution } = institutionStore;

    const date = new Date();
    const initialStartDate = new Date(date);
    initialStartDate.setMonth(initialStartDate.getMonth() - 1);
    initialStartDate.setHours(0, 0, 0, 0);

    const initialEndDate = new Date(date);
    initialEndDate.setHours(23, 59, 59, 999);

    const { control, watch, } = useForm({
        defaultValues: {
            startDate: initialStartDate,
            endDate: initialEndDate,
        },
    });

    const paginationString = sessionStorage.getItem("notification-log-pagination");
    const paginationParams = paginationString
        ? JSON.parse(paginationString!)
        : null
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        pageSize: 10,
    });

    const [popperOpener, setPopperOpener] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const firstDay = watch('startDate');
    const lastDay = watch('endDate');
    const popperAnchorRef = useRef<HTMLButtonElement>(null);
    const shouldClearLogsRef = useRef(false);

    const handlePageChange = async (page: number, pageSize: number) => {
        setPagination({ page: page + 1, pageSize });
        fetchNotificationLogs({ clearLogs: false });
    }

    const handleSelect = async () => {
        setPagination({ page: 1, pageSize: 10 });
    };

    useEffect(() => {
        shouldClearLogsRef.current = true;
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [firstDay, lastDay, selectedInstitution, notificationLogStore.search]);

    useEffect(() => {
        const clearLogs = shouldClearLogsRef.current;
        shouldClearLogsRef.current = false;
        fetchNotificationLogs({ clearLogs });
    }, [pagination]);

    const fetchNotificationLogs = async ({ clearLogs = false }) => {
        const startDate = new Date(firstDay);
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(lastDay);
        endDate.setHours(23, 59, 59, 999);

        const range: DateRange = {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
        }
        await notificationLogStore.getNotificationLogs(
            {
                paging: {
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                },
            },
            selectedInstitution,
            range, clearLogs
        );
    };

    const handlePreviewSelect = useCallback(
        (event: SyntheticEvent<HTMLElement>, logId: string) => {
            const log = logList?.find((log) => log.id === logId);
            if (!log) return;
            templateStore.setContent(log.templateContent);
            setAnchorEl(event.currentTarget);
            setPopperOpener(true);
        },
        [logList, templateStore]
    );


    const columns = useMemo(
        () =>
            getNotificationLogColumns({
                i18nLang: i18n.language,
                selectedUserInstitution,
                onPreviewSelect: handlePreviewSelect,
            }),
        [i18n.language, selectedUserInstitution, handlePreviewSelect]
    );


    return (
        <Container maxWidth="xl">
            <Typography variant="h5" component="h1" sx={{ py: 2 }} >{t("NOTIFICATIONS_LOGS")}</Typography>
            <Grid container component={"form"} sx={{ display: "flex", alignItems: "center", maxWidth: "500px", mb: 2 }}>
                <Grid item xs={4} mr={2}>
                    <FormInputDate
                        name="startDate"
                        control={control}
                        date={initialStartDate}
                        label={`${t("EVENT_START_DATE")}`}
                        onSelect={handleSelect}
                    />
                </Grid>
                <Grid item xs={4} mr={2}>
                    <FormInputDate
                        name="endDate"
                        control={control}
                        date={initialEndDate}
                        label={`${t("EVENT_END_DATE")}`}
                        onSelect={handleSelect}
                        dateLimit={firstDay}
                    />
                </Grid>
            </Grid>
            <GenericTable<NotificationResultLog>
                toolbar={<NotificationLogsTableToolbar />}
                columns={columns}
                rows={logList || []}
                props={{
                    containerProps: { className: "survey-table-container" },
                    tableContainerProps: { className: "survey-table-container-data" },
                    tableHeadProps: { className: "survey-table-header" },
                    rowProps: { className: "survey-row" },
                }}
                onPaginationModelChange={handlePageChange}
                rowCount={paginationParams?.TotalCount ?? logList.length}
                rowKey="id"
                usePagination
                loading={loading}
            />
            <ConfirmDialog />
            <CustomPopper
                open={popperOpener}
                anchorElement={anchorEl}
                children={<ViewHTMLContent />}
                close={() => setPopperOpener(false)}
            />
        </Container>
    );
}

export default observer(NotificationLogsTable);